import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, darf, datum, menge, zeitpunkt, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import {
  Auswahl, Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import Abwurf from '../teile/Abwurf.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { extern } from '../teile/extern.ts';
import Bildzoom from '../teile/Bildzoom.tsx';
import { eingeben } from '../teile/rueckfrage.tsx';

/* MATERIAL (.249).
 * ============================================================================
 * ORIGINALTEXT MARVIN (28.09.2026)
 *   „ich will das alles in die MDS FTTH struktur, datenbank, als menüpunkt,
 *    sauber dort abgelegt. aber auch die möglichkeiten manuell neue artikel
 *    hinzuzufügen"
 *
 * Ein Menüpunkt, sieben Reiter. Der Bestand wird gerechnet und nie getippt:
 * letzte Zählung plus Eingänge minus Entnahmen danach. Gebucht wird in der
 * nächsten Stufe; bis dahin zeigt diese Seite, was übernommen ist, und lässt
 * Artikel anlegen, ändern und bebildern. */

type Reiter = 'bestand' | 'katalog' | 'bestellungen' | 'wareneingang' | 'entnahmen' | 'inventur' | 'uebernahme';

const LAGE: Record<string, { text: string; klasse: string }> = {
  ok: { text: 'ok', klasse: 'merker gruen' },
  nachbestellen: { text: 'nachbestellen', klasse: 'merker warnung' },
  minus: { text: 'unter null', klasse: 'merker fehler' },
};

const QUELLE: Record<string, string> = {
  bot: 'Telegram', nachbuchung: 'Nachbuchung', hand: 'von Hand', uebernahme: 'Übernahme',
  lieferschein: 'Lieferschein',
};

function Bild({ id, weit }: { id: string | null | undefined; weit?: boolean }) {
  if (!id) return <div className={weit ? 'matbild weit leer' : 'matbild leer'} aria-hidden />;
  return <img className={weit ? 'matbild weit' : 'matbild'} loading="lazy" alt=""
              src={api.materialBildAdresse(id)} />;
}

/** Ein Vorschaubild, das sich auf Klick vergrößert (.251). Der Klick geht nicht an die Zeile darunter. */
function ZoomBild({ id, titel }: { id: string | null | undefined; titel: string }) {
  const [auf, setAuf] = useState(false);
  if (!id) return <Bild id={null} />;
  return (
    <>
      <button type="button" className="bildknopf" aria-label="Bild vergrößern"
              onClick={(e) => { e.stopPropagation(); setAuf(true); }}><Bild id={id} /></button>
      {auf && (
        <span onClick={(e) => e.stopPropagation()}>
          <Bildzoom titel={titel} adressen={[api.materialBildAdresse(id)]} zu={() => setAuf(false)} />
        </span>
      )}
    </>
  );
}

/* DIE SORTIERUNG, IM PROFIL GEMERKT (.251).
 * ============================================================================
 * ORIGINALTEXT MARVIN (28.09.2026, zum Bestand)
 *   „ich will noch eine sorierung des titels haben, den ich frei wählen kann,
 *    der aber in meinem profil gespeichert sein soll."
 *
 * Ein Klick auf eine Spaltenüberschrift sortiert danach, ein zweiter dreht
 * die Richtung. Gemerkt wird am Zugang (`benutzer.ansicht`, derselbe Weg wie
 * das Menü), nicht im Gerät: wer sich an einem anderen Rechner anmeldet,
 * findet seine Sortierung wieder. */
type Sortierung = { spalte: string; ab: boolean };
const gemerkteSortierung = new Map<string, Sortierung>();

function useSortierung(ich: Anmelder, bildschirm: string, anfang: Sortierung): [Sortierung, (spalte: string) => void] {
  const [s, setS] = useState<Sortierung>(() =>
    gemerkteSortierung.get(bildschirm) ?? (ich as any).ansicht?.[bildschirm]?.sortierung ?? anfang);
  const waehle = (spalte: string) => {
    const neu = { spalte, ab: s.spalte === spalte ? !s.ab : false };
    setS(neu);
    gemerkteSortierung.set(bildschirm, neu);
    api.ansichtMerken(bildschirm, { sortierung: neu }).catch(() => { /* die Liste sortiert trotzdem */ });
  };
  return [s, waehle];
}

/** Sortiert nach einer Spalte: Zahlen als Zahlen, Text nach deutscher Ordnung, Leeres ans Ende. */
function sortiert<T>(liste: T[], s: Sortierung, wert: (z: T, spalte: string) => unknown): T[] {
  const r = s.ab ? -1 : 1;
  return [...liste].sort((x, y) => {
    const a = wert(x, s.spalte), b = wert(y, s.spalte);
    const la = a === null || a === undefined || a === '', lb = b === null || b === undefined || b === '';
    if (la || lb) return la === lb ? 0 : la ? 1 : -1;
    const na = typeof a === 'number' ? a : Number.NaN, nb = typeof b === 'number' ? b : Number.NaN;
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * r;
    return String(a).localeCompare(String(b), 'de', { numeric: true }) * r;
  });
}

/** Eine Spaltenüberschrift, die sortiert. */
function SortKopf({ spalte, s, waehle, zahl, breite, children }: {
  spalte: string; s: Sortierung; waehle: (spalte: string) => void; zahl?: boolean; breite?: number; children: ReactNode;
}) {
  const an = s.spalte === spalte;
  return (
    <th className={zahl ? 'zahl sortierbar' : 'sortierbar'} style={breite ? { width: breite } : undefined}
        aria-sort={an ? (s.ab ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className={an ? 'sortknopf an' : 'sortknopf'} onClick={() => waehle(spalte)}
              title="Klick sortiert, ein zweiter Klick dreht die Richtung. Gemerkt in deinem Profil.">
        {children}<span className="sortpfeil" aria-hidden>{an ? (s.ab ? '▼' : '▲') : '↕'}</span>
      </button>
    </th>
  );
}

/** Suchen über Mat-Nr, Text und Beschreibung. Mehrere Wörter: alle müssen passen. */
function passt(z: any, suche: string): boolean {
  const w = suche.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!w.length) return true;
  const t = `${z.mat_nr ?? ''} ${z.kurztext ?? ''} ${z.beschreibung ?? ''} ${z.kategorie ?? ''} ${z.bezeichnung ?? ''} ${z.firma ?? ''}`.toLowerCase();
  return w.every((x) => t.includes(x));
}

export default function Material({ ich }: { ich: Anmelder }) {
  /* WER NUR SEINE EIGENEN ENTNAHMEN SIEHT (.250).
     Marvin, 28.09.2026: „die können nur ihre entnahmen sehen." Eine
     Partnerfirma bekommt deshalb EINEN Reiter und keinen Bestand; die Grenze
     selbst steht im Server, nicht hier. */
  const lesen = darf(ich, 'material.lesen') && !ich.istSub;
  const [reiter, setReiter] = useState<Reiter>(lesen ? 'bestand' : 'entnahmen');
  const [artikel, setArtikel] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [entnahme, setEntnahme] = useState(false);
  const pflegen = darf(ich, 'material.pflegen');
  const buchen = darf(ich, 'material.buchen') || darf(ich, 'material.eigene');

  const R = (k: Reiter, t: string) => (
    <button aria-current={reiter === k} onClick={() => setReiter(k)}>{t}</button>
  );

  return (
    <>
      <Kopf titel="Material" weg={lesen ? 'Lager Andernach' : `Entnahmen${ich.firma ? ' von ' + ich.firma : ''}`}
            rechts={(
              <div className="reihe" style={{ gap: 8 }}>
                {buchen && (
                  <button className={pflegen ? 'knopf' : 'knopf haupt'} onClick={() => setEntnahme(true)}>
                    {lesen ? 'Entnahme buchen' : 'Entnahme nachbuchen'}
                  </button>
                )}
                {pflegen && <button className="knopf haupt" onClick={() => setNeu(true)}>Neuer Artikel</button>}
              </div>
            )} />
      <div className="inhalt">
        {lesen && (
          <div className="reiter">
            {R('bestand', 'Bestand')}
            {R('katalog', 'Katalog')}
            {R('bestellungen', 'Bestellungen')}
            {R('wareneingang', 'Wareneingang')}
            {R('entnahmen', 'Entnahmen')}
            {R('inventur', 'Inventur')}
            {pflegen && R('uebernahme', 'Übernahme')}
          </div>
        )}
        {lesen && reiter === 'bestand' && <Bestand ich={ich} oeffne={setArtikel} />}
        {lesen && reiter === 'katalog' && <Katalog ich={ich} oeffne={setArtikel} />}
        {lesen && reiter === 'bestellungen' && <Bestellungen ich={ich} oeffne={setArtikel} />}
        {lesen && reiter === 'wareneingang' && <Wareneingang ich={ich} oeffne={setArtikel} />}
        {reiter === 'entnahmen' && <Entnahmen ich={ich} oeffne={lesen ? setArtikel : undefined} />}
        {lesen && reiter === 'inventur' && <Inventur ich={ich} oeffne={setArtikel} />}
        {lesen && reiter === 'uebernahme' && pflegen && <Uebernahme />}
      </div>
      {artikel && <ArtikelFenster id={artikel} ich={ich} zu={() => setArtikel(null)} />}
      {neu && (
        <ArtikelFormular titel="Neuer Artikel" anfang={{ einheit: 'ST', inhalt_je_einheit: '1', nachbestellfaktor: '2' }}
          mitNummer zu={() => setNeu(false)} oeffne={(id) => { setNeu(false); setArtikel(id); }}
          speichern={async (w, bilder) => {
            const r = await api.materialArtikelNeu(w);
            if (bilder.length) await api.materialBilderHoch(r.id, bilder);
            setNeu(false); setArtikel(r.id);
          }} />
      )}
      {entnahme && <EntnahmeFenster ich={ich} zu={() => setEntnahme(false)} />}
    </>
  );
}

// ============================================================================
// Einen Artikel wählen
// ============================================================================

/**
 * DIE ARTIKELSUCHE (.251): tippen, die Liste klappt darunter auf.
 *
 * ORIGINALTEXT MARVIN (28.09.2026)
 *   „überall auf der webseite soll eine livesuche und dropdown bei den
 *    artikeln sein. und wenn ich einen artikel auswähle soll er mir auch die
 *    einheiten vorschlagen"
 *
 * Jede Stelle, an der ein Artikel gewählt wird, nimmt DIESES Feld: Entnahme,
 * Warenkorb, Wareneingang, Vorlage. Gesucht wird über Mat-Nr, Text und
 * Kategorie, mehrere Wörter müssen alle passen. Pfeiltasten und Enter gehen,
 * Escape klappt zu. Die Treffer zeigen Bild, Mat-Nr und, wer das Lager
 * sieht, den Bestand.
 */
function ArtikelWahl({ liste, wert, setze, fehler, name = 'artikel_id', titel = 'Artikel', pflicht = true,
                      platzhalter = 'Mat-Nr oder Bezeichnung tippen', autoFocus = true }: {
  liste: any[]; wert: any | null; setze: (a: any | null) => void; fehler?: Fehlerlage | null; name?: string;
  titel?: string; pflicht?: boolean; platzhalter?: string; autoFocus?: boolean;
}) {
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState(0);
  const [zoom, setZoom] = useState<string | null>(null);
  const treffer = useMemo(() => {
    const q = suche.trim();
    if (!q) return liste.slice(0, 12);
    return liste.filter((a) => passt(a, q)).sort((x, y) => {
      // Eine genau getippte Mat-Nr steht oben.
      const gx = String(x.mat_nr) === q ? 0 : String(x.mat_nr).startsWith(q) ? 1 : 2;
      const gy = String(y.mat_nr) === q ? 0 : String(y.mat_nr).startsWith(q) ? 1 : 2;
      return gx - gy;
    }).slice(0, 12);
  }, [liste, suche]);
  const waehle = (a: any) => { setze(a); setSuche(''); setOffen(false); setMarkiert(0); };
  if (wert) {
    return (
      <div className="feld" style={{ width: '100%' }}>
        <label>{titel}</label>
        <div className="artgewaehlt">
          <button type="button" className="bildknopf" onClick={() => wert.bild_id && setZoom(wert.bild_id)} aria-label="Bild vergrößern">
            <Bild id={wert.bild_id} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div><b>{wert.mat_nr}</b> {wert.kurztext}</div>
            <div className="klein leise">
              {[wert.kategorie, `Lager in ${wert.einheit}${Number(wert.inhalt_je_einheit) > 1 ? ` à ${menge(Number(wert.inhalt_je_einheit))} Stück` : ''}`,
                wert.bme && wert.bme !== wert.einheit ? `bestellt in ${wert.bme}${wert.vpe ? ` (${wert.vpe})` : ''}` : null,
                wert.bestand !== undefined && wert.bestand !== null ? `Bestand ${menge(Number(wert.bestand))} ${wert.einheit}` : null]
                .filter(Boolean).join(', ')}
            </div>
          </div>
          <button type="button" className="knopf klein" onClick={() => { setze(null); setSuche(''); }}>ändern</button>
        </div>
        {zoom && <Bildzoom titel={wert.kurztext} adressen={[api.materialBildAdresse(zoom)]} zu={() => setZoom(null)} />}
      </div>
    );
  }
  return (
    <div className="feld artsuche" style={{ width: '100%' }}>
      <label htmlFor={name}>{titel} {pflicht && <span className="pflicht">*</span>}</label>
      <input id={name} type="search" autoComplete="off" value={suche} autoFocus={autoFocus}
             role="combobox" aria-expanded={offen} aria-controls={`${name}_liste`}
             placeholder={platzhalter}
             onClick={() => setOffen(true)}
             onBlur={() => setTimeout(() => setOffen(false), 150)}
             onChange={(e) => { setSuche(e.target.value); setOffen(true); setMarkiert(0); }}
             onKeyDown={(e) => {
               if (e.key === 'ArrowDown') { e.preventDefault(); setOffen(true); setMarkiert((m) => Math.min(m + 1, treffer.length - 1)); }
               else if (e.key === 'ArrowUp') { e.preventDefault(); setMarkiert((m) => Math.max(m - 1, 0)); }
               else if (e.key === 'Enter') { if (offen && treffer[markiert]) { e.preventDefault(); waehle(treffer[markiert]); } }
               else if (e.key === 'Escape') { if (offen) { e.stopPropagation(); setOffen(false); } }
             }} />
      {fehler?.feld === name && <div className="feldfehler">{fehler.text}</div>}
      {offen && (
        <div className="artsuche-liste" id={`${name}_liste`} role="listbox">
          {!liste.length && <div className="artsuche-leer">Die Artikel werden geladen …</div>}
          {!!liste.length && !treffer.length && <div className="artsuche-leer">Kein Artikel passt zu „{suche}".</div>}
          {treffer.map((a, i) => (
            <div key={a.id} role="option" aria-selected={i === markiert}
                 className={i === markiert ? 'artsuche-zeile aktiv' : 'artsuche-zeile'}
                 onMouseEnter={() => setMarkiert(i)}
                 onMouseDown={(e) => { e.preventDefault(); waehle(a); }}>
              <Bild id={a.bild_id} />
              <div className="artsuche-text">
                <div><b>{a.mat_nr}</b> {a.kurztext}</div>
                <div className="klein leise">{[a.kategorie, a.einheit + (Number(a.inhalt_je_einheit) > 1 ? ` à ${menge(Number(a.inhalt_je_einheit))}` : '')].filter(Boolean).join(', ')}</div>
              </div>
              {a.bestand !== undefined && a.bestand !== null && (
                <div className="artsuche-bestand">
                  <span className={LAGE[a.lage]?.klasse ?? 'merker'}>{menge(Number(a.bestand))} {a.einheit}</span>
                </div>
              )}
            </div>
          ))}
          {!suche.trim() && liste.length > 12 && <div className="artsuche-leer">Tippen grenzt ein ({liste.length} Artikel).</div>}
        </div>
      )}
    </div>
  );
}

/** Welche Einheiten zu einem Artikel passen, mit einem Satz dazu. */
function einheitenFuer(a: any, zweck: 'entnahme' | 'bestellung' | 'eingang'): { wert: string; text: string; hinweis: string }[] {
  if (!a) return [];
  const einheit = a.einheit ?? a.lager_einheit ?? 'ST';
  const inhalt = Number(a.inhalt_je_einheit ?? 1);
  const raus = [{ wert: einheit, text: einheit, hinweis: inhalt > 1 ? `je ${menge(inhalt)} Stück` : 'Lagereinheit' }];
  if (inhalt > 1 && einheit !== 'ST') raus.push({ wert: 'ST', text: 'Stück', hinweis: `${menge(inhalt)} Stück = 1 ${einheit}` });
  if (zweck !== 'entnahme' && a.bme && a.bme !== einheit && !raus.some((x) => x.wert === a.bme)) {
    raus.push({ wert: a.bme, text: a.bme, hinweis: `Bestelleinheit${a.vpe ? `, ${a.vpe}` : ''}` });
  }
  return raus;
}

/** Die Einheiten zum Anklicken. Die erste ist vorgewählt. */
function EinheitWahl({ artikel, wert, setze, zweck }: {
  artikel: any; wert: string; setze: (v: string) => void; zweck: 'entnahme' | 'bestellung' | 'eingang';
}) {
  const liste = einheitenFuer(artikel, zweck);
  if (!liste.length) return null;
  const jetzt = wert || liste[0].wert;
  return (
    <div className="feld">
      <label>Einheit</label>
      <div className="einheitwahl" role="radiogroup">
        {liste.map((e) => (
          <button type="button" key={e.wert} role="radio" aria-checked={jetzt === e.wert}
                  className={jetzt === e.wert ? 'einheit an' : 'einheit'} onClick={() => setze(e.wert)}
                  title={e.hinweis}>
            <b>{e.text}</b><span>{e.hinweis}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const heute = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });

function EntnahmeFenster({ ich, zu }: { ich: Anmelder; zu: () => void }) {
  const [liste, setListe] = useState<any[]>([]);
  const [firmen, setFirmen] = useState<any[]>([]);
  const [w, setW] = useState<any>({ tag: heute(), menge: '', einheit: '', subunternehmer_id: '' });
  const [artikel, setArtikel] = useState<any | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState<string | null>(null);
  const innen = !ich.istSub && darf(ich, 'material.buchen');
  useEffect(() => {
    api.materialAuswahl().then(setListe).catch((f) => setFehler(ausFehler(f)));
    if (innen) api.subunternehmer().then((r) => setFirmen(r.filter((f: any) => f.aktiv))).catch(() => {});
  }, []);
  async function los() {
    setLaeuft(true); setFehler(null);
    try {
      if (!artikel) throw Object.assign(new Error('Bitte einen Artikel wählen.'), { daten: { feld: 'artikel_id' } });
      await api.materialEntnahmeBuchen({ ...w, artikel_id: artikel.id, einheit: w.einheit || artikel.einheit,
                                         subunternehmer_id: innen ? w.subunternehmer_id : undefined });
      setFertig(`${w.menge} ${w.einheit || artikel.einheit} ${artikel.kurztext} gebucht.`);
      setArtikel(null); setW({ ...w, menge: '', einheit: '' });
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={innen ? 'Entnahme buchen' : 'Entnahme nachbuchen'} breite={640} laeuft={laeuft} zu={zu}>
      <form onSubmit={(e) => { e.preventDefault(); if (!laeuft) los(); }}>
        <Fehlerkasten lage={fehler} />
        {fertig && <div className="hinweis">✅ {fertig} Die nächste?</div>}
        {innen && (
          <div className="feldreihe">
            <Auswahl name="subunternehmer_id" titel="Firma" pflicht breite={300} wert={w.subunternehmer_id}
                     setze={(v) => setW({ ...w, subunternehmer_id: v })} fehler={fehler}
                     optionen={firmen.map((f) => ({ wert: f.id, text: f.firma }))} />
          </div>
        )}
        <ArtikelWahl liste={liste} wert={artikel} setze={(a) => { setArtikel(a); setW({ ...w, einheit: '' }); }} fehler={fehler} />
        <div className="feldreihe" style={{ alignItems: 'flex-start' }}>
          <Text name="menge" titel="Menge" pflicht breite={120} wert={w.menge} fehler={fehler}
                setze={(v) => setW({ ...w, menge: v })} />
          {artikel && (
            <EinheitWahl artikel={artikel} zweck="entnahme" wert={w.einheit} setze={(v) => setW({ ...w, einheit: v })} />
          )}
          <Text name="tag" titel="Tag" art="date" breite={170} wert={w.tag} fehler={fehler}
                setze={(v) => setW({ ...w, tag: v })} />
        </div>
        <div className="reihe" style={{ marginTop: 14, gap: 8 }}>
          <button className="knopf haupt" disabled={laeuft}>{laeuft ? 'Einen Moment' : 'Buchen'}</button>
          <button type="button" className="knopf" disabled={laeuft} onClick={zu}>Schließen</button>
        </div>
      </form>
    </Dialog>
  );
}

// ============================================================================
// Bestand
// ============================================================================

function Bestand({ ich, oeffne }: { ich: Anmelder; oeffne: (id: string) => void }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [suche, setSuche] = useState('');
  const [nurKnapp, setNurKnapp] = useState(false);
  async function laden() {
    try { setZeilen(await api.materialBestand()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const [sort, sortiere] = useSortierung(ich, 'material_bestand', { spalte: 'standard', ab: false });
  const LAGE_REIHE: Record<string, number> = { minus: 0, nachbestellen: 1, ok: 2 };
  const sicht = sortiert((zeilen ?? []).filter((z) => passt(z, suche) && (!nurKnapp || z.lage !== 'ok')), sort, (z: any, k) =>
    k === 'standard' ? null
      : k === 'lage' ? LAGE_REIHE[z.lage] ?? 3
      : ['bestand', 'stueck', 'mindestbestand', 'verbrauch_30', 'reichweite_tage', 'vorschlag'].includes(k)
        ? (z[k] === null || z[k] === undefined ? null : Number(z[k]))
      : k === 'gezaehlt_am' ? z.gezaehlt_am
      : z[k]);
  const knapp = (zeilen ?? []).filter((z) => z.lage !== 'ok').length;
  return (
    <Karte titel={zeilen ? `Bestand (${zeilen.length} Artikel)` : 'Bestand'}
           rechts={<input type="search" autoComplete="off" placeholder="Mat-Nr oder Bezeichnung"
                          value={suche} onChange={(e) => setSuche(e.target.value)} />}>
      <Fehlerkasten lage={fehler} />
      <div className="reihe" style={{ marginBottom: 10 }}>
        <Haken name="knapp" titel={`nur Nachbestellen und unter null (${knapp})`}
               wert={nurKnapp} setze={setNurKnapp} />
      </div>
      {zeilen && !zeilen.length && (
        <Leer text={'Noch kein Bestand. Unter „Übernahme" die bisherige Materialliste einlesen.'} />
      )}
      {!!sicht.length && (
        <div className="rollt">
          <table className="liste mattabelle">
            <thead>
              <tr>
                <th style={{ width: 56 }}></th>
                <SortKopf spalte="mat_nr" s={sort} waehle={sortiere} breite={96}>Mat-Nr</SortKopf>
                <SortKopf spalte="kurztext" s={sort} waehle={sortiere}>Bezeichnung</SortKopf>
                <SortKopf spalte="bestand" s={sort} waehle={sortiere} zahl>Bestand</SortKopf>
                <SortKopf spalte="stueck" s={sort} waehle={sortiere} zahl>Stück</SortKopf>
                <SortKopf spalte="mindestbestand" s={sort} waehle={sortiere} zahl>Mindest</SortKopf>
                <SortKopf spalte="verbrauch_30" s={sort} waehle={sortiere} zahl>Verbrauch 30 T</SortKopf>
                <SortKopf spalte="reichweite_tage" s={sort} waehle={sortiere} zahl>Reichweite</SortKopf>
                <SortKopf spalte="vorschlag" s={sort} waehle={sortiere} zahl>Vorschlag</SortKopf>
                <SortKopf spalte="lage" s={sort} waehle={sortiere}>Lage</SortKopf>
                <SortKopf spalte="gezaehlt_am" s={sort} waehle={sortiere}>gezählt</SortKopf>
              </tr>
            </thead>
            <tbody>
              {sicht.map((z) => (
                <tr key={z.artikel_id} className="anklickbar" onClick={() => oeffne(z.artikel_id)}>
                  <td><ZoomBild id={z.bild_id} titel={z.kurztext} /></td>
                  <td className="klein">{z.mat_nr}</td>
                  <td>{z.kurztext}{z.standard && <span className="merker leise" style={{ marginLeft: 6 }}>Standard</span>}</td>
                  <td className="zahl"><b>{menge(Number(z.bestand))}</b> {z.einheit}</td>
                  <td className="zahl">{Number(z.inhalt_je_einheit) !== 1 ? menge(Number(z.stueck)) : ''}</td>
                  <td className="zahl">{z.mindestbestand !== null ? menge(Number(z.mindestbestand)) : ''}</td>
                  <td className="zahl">{Number(z.verbrauch_30) ? menge(Number(z.verbrauch_30)) : ''}</td>
                  <td className="zahl">{z.reichweite_tage !== null ? `${z.reichweite_tage} Tage` : ''}</td>
                  <td className="zahl">{Number(z.vorschlag) ? menge(Number(z.vorschlag)) : ''}</td>
                  <td><span className={LAGE[z.lage]?.klasse ?? 'merker'}>{LAGE[z.lage]?.text ?? z.lage}</span></td>
                  <td className="klein leise">
                    {z.gezaehlt_am ? datum(z.gezaehlt_am) : ''}
                    {z.zaehlart === 'uebernahme' ? ' (Übernahme)' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Karte>
  );
}

// ============================================================================
// Katalog
// ============================================================================

function Katalog({ ich, oeffne }: { ich: Anmelder; oeffne: (id: string) => void }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [suche, setSuche] = useState('');
  const [nurLager, setNurLager] = useState(false);
  const [nurEigene, setNurEigene] = useState(false);
  async function laden() {
    try { setZeilen(await api.materialArtikel()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const [sort, sortiere] = useSortierung(ich, 'material_katalog', { spalte: 'standard', ab: false });
  const sicht = useMemo(() => sortiert((zeilen ?? []).filter((z) => passt(z, suche)
    && (!nurLager || z.bestand !== null) && (!nurEigene || z.herkunft === 'manuell')), sort, (z: any, k) =>
      k === 'standard' ? null : k === 'bestand' ? (z.bestand === null ? null : Number(z.bestand)) : z[k]),
    [zeilen, suche, nurLager, nurEigene, sort]);
  const GRENZE = 150;
  return (
    <Karte titel={zeilen ? `Katalog (${zeilen.length} Artikel)` : 'Katalog'}
           rechts={<input type="search" autoComplete="off" placeholder="z. B. SNR 7, OneBox, EZA"
                          value={suche} onChange={(e) => setSuche(e.target.value)} />}>
      <Fehlerkasten lage={fehler} />
      {/* ETIKETT WEICHT VOM KATALOG AB (.359). Aus der .358 offen: „OneBox
          9-12, Etikett 5 Stk gegenüber Katalog Pack à 10". */}
      {(zeilen ?? []).some((z) => z.vpe_etikett) && (
        <div className="hinweis warnung" style={{ marginBottom: 10 }}>
          <b>Packgröße auf dem Etikett weicht vom Katalog ab</b>
          {(zeilen ?? []).filter((z) => z.vpe_etikett).map((z) => (
            <div key={z.id} className="reihe" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
              <span>{z.kurztext}: Etikett <b>{z.vpe_etikett} Stück</b>, Katalog „{z.vpe || 'ohne Angabe'}"</span>
              {darf(ich, 'material.pflegen') && <>
                <button className="knopf klein" onClick={async () => { try { await api.materialVpeEtikett(z.id, 'uebernehmen'); await laden(); } catch (f) { setFehler(ausFehler(f)); } }}>Übernehmen</button>
                <button className="knopf klein" onClick={async () => { try { await api.materialVpeEtikett(z.id, 'passt'); await laden(); } catch (f) { setFehler(ausFehler(f)); } }}>Passt so</button>
              </>}
            </div>
          ))}
        </div>
      )}
      <div className="reihe" style={{ marginBottom: 10, gap: 18 }}>
        <Haken name="lager" titel="nur Artikel im Lager" wert={nurLager} setze={setNurLager} />
        <Haken name="eigene" titel="nur eigene (nicht im Telekom-Katalog)" wert={nurEigene} setze={setNurEigene} />
      </div>
      {zeilen && !sicht.length && <Leer text="Kein Artikel passt zur Suche." />}
      {!!sicht.length && (
        <div className="rollt">
          <table className="liste mattabelle">
            <thead>
              <tr>
                <th style={{ width: 56 }}></th>
                <SortKopf spalte="mat_nr" s={sort} waehle={sortiere} breite={96}>Mat-Nr</SortKopf>
                <SortKopf spalte="kurztext" s={sort} waehle={sortiere}>Bezeichnung</SortKopf>
                <SortKopf spalte="kategorie" s={sort} waehle={sortiere} breite={160}>Kategorie</SortKopf>
                <SortKopf spalte="einheit" s={sort} waehle={sortiere} breite={90}>Einheit</SortKopf>
                <SortKopf spalte="bestand" s={sort} waehle={sortiere} zahl breite={110}>Bestand</SortKopf>
              </tr>
            </thead>
            <tbody>
              {sicht.slice(0, GRENZE).map((z) => (
                <tr key={z.id} className="anklickbar" onClick={() => oeffne(z.id)}
                    style={{ opacity: z.aktiv ? 1 : 0.5 }}>
                  <td><ZoomBild id={z.bild_id} titel={z.kurztext} /></td>
                  <td className="klein">{z.mat_nr}</td>
                  <td>
                    {z.kurztext}
                    {z.herkunft === 'manuell' && <span className="merker blau" style={{ marginLeft: 6 }}>eigener Artikel</span>}
                    {!z.aktiv && <span className="merker leise" style={{ marginLeft: 6 }}>ausgesetzt</span>}
                    {z.beschreibung && <div className="klein leise zweizeilig">{z.beschreibung}</div>}
                  </td>
                  <td className="klein">{z.kategorie ?? ''}</td>
                  <td className="klein">{z.einheit}{Number(z.inhalt_je_einheit) !== 1 ? ` à ${menge(Number(z.inhalt_je_einheit))}` : ''}</td>
                  <td className="zahl">{z.bestand !== null ? menge(Number(z.bestand)) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sicht.length > GRENZE && (
            <p className="klein leise">Gezeigt werden {GRENZE} von {sicht.length}. Die Suche grenzt ein.</p>
          )}
        </div>
      )}
    </Karte>
  );
}

// ============================================================================
// Ein Artikel
// ============================================================================

function ArtikelFenster({ id, ich, zu }: { id: string; ich: Anmelder; zu: () => void }) {
  const [a, setA] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [aendern, setAendern] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const pflegen = darf(ich, 'material.pflegen');
  const bestellen = darf(ich, 'material.bestellen');
  const [zoom, setZoom] = useState<number | null>(null);
  const [korb, setKorb] = useState<{ menge: string; einheit: string }>({ menge: '', einheit: '' });
  const [imKorb, setImKorb] = useState<string | null>(null);
  async function laden() {
    try { setA(await api.materialArtikelEins(id)); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  /* IN DEN WARENKORB (.251). Marvin: „wo kann ich, wenn ich artikel öffne
     diese zum warenkorb hinzufügen?" - hier, direkt unter den Angaben. */
  async function inDenKorb() {
    setLaeuft(true); setFehler(null); setImKorb(null);
    try {
      const r = await api.materialInDenWarenkorb([{ artikel_id: id, menge: korb.menge || undefined, einheit: korb.einheit || a.einheit }]);
      setImKorb(`Liegt im Warenkorb (Bestellung ${r.nummer}, ${r.positionen} ${r.positionen === 1 ? 'Position' : 'Positionen'}).`);
      setKorb({ menge: '', einheit: '' });
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  useEffect(() => { laden(); }, [id]);
  useFrisch(laden);

  async function bilderHoch(dateien: File[]) {
    setLaeuft(true); setFehler(null);
    try { await api.materialBilderHoch(id, dateien); await laden(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  async function bildWeg(bild: string) {
    setLaeuft(true); setFehler(null);
    try { await api.materialBildWeg(bild); await laden(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  if (aendern && a) {
    return (
      <ArtikelFormular titel={`${a.mat_nr} ändern`} zu={() => setAendern(false)}
        anfang={Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v === null ? '' : typeof v === 'number' ? String(v) : v]))}
        speichern={async (w) => { await api.materialArtikelAendern(id, w); setAendern(false); await laden(); }} />
    );
  }

  const b = a?.bestand;
  return (
    <Dialog titel={a ? a.kurztext : 'Artikel'} untertitel={a ? `Mat-Nr ${a.mat_nr}` : undefined}
            breite={900} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {a && (
        <>
          <div className="matkopf">
            <div className="matbilder">
              {a.bilder.map((x: any, i: number) => (
                <div key={x.id} className="matbildrahmen">
                  <button type="button" className="bildknopf" aria-label="Bild vergrößern" onClick={() => setZoom(i)}>
                    <img className="matbild weit" alt="" src={api.materialBildAdresse(x.id)} />
                  </button>
                  {pflegen && (
                    <button className="knopf klein" disabled={laeuft} onClick={() => bildWeg(x.id)}>entfernen</button>
                  )}
                </div>
              ))}
              {!a.bilder.length && <Bild id={null} weit />}
            </div>
            <div className="matangaben">
              <dl className="felder">
                <dt>Bestand</dt>
                <dd>{b ? <b>{menge(Number(b.bestand), a.einheit)}</b> : 'noch nicht gezählt'}
                  {b && Number(a.inhalt_je_einheit) !== 1 ? ` (${menge(Number(b.stueck))} Stück)` : ''}</dd>
                {b?.gezaehlt_am && (<><dt>Gezählt</dt><dd>{zeitpunkt(b.gezaehlt_am)}{b.zaehlart === 'uebernahme' ? ' (Stand der Übernahme)' : ''}, seither {menge(Number(b.eingang_seither))} ein, {menge(Number(b.entnommen_seither))} aus</dd></>)}
                <dt>Lagereinheit</dt><dd>{a.einheit}{Number(a.inhalt_je_einheit) !== 1 ? ` mit ${menge(Number(a.inhalt_je_einheit))} Stück` : ''}</dd>
                {a.bme && (<><dt>Bestelleinheit</dt><dd>{a.bme}{a.vpe ? `, ${a.vpe}` : ''}</dd></>)}
                <dt>Mindestbestand</dt><dd>{a.mindestbestand !== null ? menge(Number(a.mindestbestand)) : 'keiner'}</dd>
                {a.kategorie && (<><dt>Kategorie</dt><dd>{a.kategorie}</dd></>)}
                {a.ht_nr && (<><dt>HT-Nr</dt><dd>{a.ht_nr}</dd></>)}
                <dt>Herkunft</dt><dd>{a.herkunft === 'katalog' ? `Telekom-Katalog${a.katalogstand ? ` (${a.katalogstand})` : ''}` : 'eigener Artikel'}</dd>
                {a.standard && (<><dt>Standardartikel</dt><dd>{a.standard_menge !== null ? `typisch ${menge(Number(a.standard_menge))}` : 'ja'}{a.standard_bemerkung ? `, ${a.standard_bemerkung}` : ''}</dd></>)}
              </dl>
              {a.beschreibung && <p className="klein">{a.beschreibung}</p>}
              {a.bemerkung && <p className="klein leise">{a.bemerkung}</p>}
              {pflegen && (
                <div className="reihe" style={{ gap: 8, marginTop: 8 }}>
                  <button className="knopf" onClick={() => setAendern(true)}>Ändern</button>
                </div>
              )}
              {bestellen && a.aktiv !== false && (
                <form className="korbzeile" onSubmit={(e) => { e.preventDefault(); if (!laeuft) inDenKorb(); }}>
                  <Text name="korb_menge" titel="Menge" breite={100} wert={korb.menge}
                        platzhalter={String(Number(a.standard_menge) > 0 && a.bme === a.einheit ? menge(Number(a.standard_menge)) : 1)}
                        setze={(v) => setKorb({ ...korb, menge: v })} />
                  <EinheitWahl artikel={a} zweck="bestellung" wert={korb.einheit} setze={(v) => setKorb({ ...korb, einheit: v })} />
                  <button className="knopf haupt" disabled={laeuft}>In den Warenkorb</button>
                  {imKorb && <div className="hinweis" style={{ width: '100%', margin: 0 }}>✅ {imKorb}</div>}
                </form>
              )}
            </div>
          </div>
          {zoom !== null && (
            <Bildzoom titel={a.kurztext} adressen={a.bilder.map((x: any) => api.materialBildAdresse(x.id))}
                      start={zoom} zu={() => setZoom(null)} />
          )}
          {pflegen && (
            <Abwurf klein accept=".jpg,.jpeg,.png,.webp,image/*" laeuft={laeuft}
              laufText="Bild wird hinterlegt …" titel="Bild hier ablegen"
              hinweis="JPEG, PNG oder WebP. Mehrere auf einmal gehen."
              nimm={(st) => bilderHoch(st.map((x) => x.datei))} />
          )}

          <Bewegungen a={a} />
        </>
      )}
    </Dialog>
  );
}

function Bewegungen({ a }: { a: any }) {
  const zeilen = [
    ...a.eingaenge.map((w: any) => ({ zeit: w.gebucht_am, art: 'Eingang', text: `Lieferschein ${w.lieferschein_nr} / ${w.pos}`,
      menge: `+ ${menge(Number(w.menge_lager))}`, weg: !!w.storniert_am })),
    ...a.entnahmen.map((e: any) => ({ zeit: e.zeit, art: 'Entnahme', text: `${e.firma ?? 'ohne Firma'}, ${QUELLE[e.quelle] ?? e.quelle}`,
      menge: `- ${menge(Number(e.menge))} ${e.einheit ?? ''}`, weg: !!e.storniert_am })),
    ...a.zaehlungen.map((i: any) => ({ zeit: i.zeit, art: i.art === 'uebernahme' ? 'Übernahme' : 'Inventur',
      text: i.art === 'uebernahme' ? (i.bemerkung ?? '') : (i.zaehler ?? ''), menge: `= ${menge(Number(i.ist))}`, weg: false })),
  ].sort((x, y) => String(y.zeit).localeCompare(String(x.zeit)));
  return (
    <>
      <h3 className="abschnitt">Bewegungen</h3>
      {!zeilen.length && <Leer text="Noch keine Bewegung." />}
      {!!zeilen.length && (
        <p className="klein leise" style={{ margin: '0 0 6px' }}>
          Der Bestand rechnet ab der letzten Zählung. Was davor steht, ist Verlauf.
        </p>
      )}
      {!!zeilen.length && (
        <table className="liste">
          <tbody>
            {zeilen.slice(0, 60).map((z, i) => (
              <tr key={i} style={{ opacity: z.weg ? 0.45 : 1 }}>
                <td className="klein" style={{ width: 130 }}>{zeitpunkt(z.zeit)}</td>
                <td style={{ width: 100 }}>{z.art}</td>
                <td className="klein">{z.text}</td>
                <td className="zahl" style={{ width: 120 }}>{z.menge}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!!a.bestellt.length && (
        <>
          <h3 className="abschnitt">Bestellt</h3>
          <table className="liste">
            <tbody>
              {a.bestellt.map((p: any) => (
                <tr key={p.id}>
                  <td style={{ width: 220, whiteSpace: 'nowrap' }}>Nr. {p.nummer}, {datum(p.datum)}</td>
                  <td className="zahl" style={{ width: 120 }}>{menge(Number(p.menge), p.einheit ?? '')}</td>
                  <td className="klein">
                    {p.storniert ? 'storniert' : p.erledigt ? 'erledigt' : 'offen'}
                    {p.zugesagt_am ? `, zugesagt ${datum(p.zugesagt_am)}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

/** Was aus einer Vorlage übernommen wird: alles ausser Nummer und Bildern. */
const AUS_VORLAGE = ['kurztext', 'beschreibung', 'einheit', 'inhalt_je_einheit', 'bme', 'vpe', 'kategorie',
  'mindestbestand', 'nachbestellfaktor', 'standard', 'standard_menge', 'bemerkung'];

function ArtikelFormular({ titel, anfang, mitNummer, speichern, zu, oeffne }: {
  titel: string; anfang: Record<string, any>; mitNummer?: boolean;
  speichern: (w: Record<string, any>, bilder: File[]) => Promise<void>; zu: () => void;
  oeffne?: (id: string) => void;
}) {
  const [w, setW] = useState<Record<string, any>>({ ...anfang });
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [liste, setListe] = useState<any[]>([]);
  const [werte, setWerte] = useState<Record<string, string[]>>({});
  const [vorlage, setVorlage] = useState<any | null>(null);
  const [bilder, setBilder] = useState<File[]>([]);
  const setze = (k: string) => (v: any) => setW({ ...w, [k]: v });
  useEffect(() => {
    api.materialWerte().then(setWerte).catch(() => {});
    if (mitNummer) api.materialAuswahl().then(setListe).catch(() => {});
  }, [mitNummer]);
  /* DIE VORLAGE FÜLLT DAS FORMULAR. Gewählt wird aus allen Artikeln, also
     auch aus dem ganzen Telekom-Katalog; danach lässt sich alles ändern. */
  async function vorlageWaehlen(a: any | null) {
    setVorlage(a);
    if (!a) return;
    try {
      const v = await api.materialArtikelEins(a.id);
      const neu: Record<string, any> = { ...w };
      for (const k of AUS_VORLAGE) {
        const x = v[k];
        neu[k] = x === null || x === undefined ? '' : typeof x === 'number' ? String(x) : x;
      }
      setW(neu);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  const nr = String(w.mat_nr ?? '').trim();
  const schonDa = mitNummer && nr ? liste.find((a) => String(a.mat_nr) === nr) : null;
  async function los() {
    setLaeuft(true); setFehler(null);
    const felder = ['kurztext', 'beschreibung', 'einheit', 'inhalt_je_einheit', 'bme', 'vpe', 'kategorie',
      'mindestbestand', 'nachbestellfaktor', 'standard', 'standard_menge', 'bemerkung', 'aktiv'];
    const raus: Record<string, any> = {};
    for (const k of felder) if (k in w) raus[k] = w[k];
    if (mitNummer) raus.mat_nr = w.mat_nr ?? '';
    try { await speichern(raus, bilder); } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={titel} breite={720} laeuft={laeuft} zu={zu}>
      <form onSubmit={(e) => { e.preventDefault(); if (!laeuft) los(); }}>
        <Fehlerkasten lage={fehler} />
        {mitNummer && (
          <>
            <ArtikelWahl liste={liste} wert={vorlage} setze={vorlageWaehlen} name="vorlage" titel="Vorlage"
                         pflicht={false} platzhalter="Aus dem Katalog: Mat-Nr oder Bezeichnung" />
            <div className="feldhinweis" style={{ marginBottom: 8 }}>
              {vorlage
                ? 'Die Angaben der Vorlage stehen unten und lassen sich ändern. Die Mat-Nr trägst du selbst ein.'
                : 'Ein ähnlicher Artikel als Vorlage füllt alle Felder vor. Ohne Vorlage geht es auch.'}
            </div>
          </>
        )}
        <div className="feldreihe">
          {mitNummer && (
            <Text name="mat_nr" titel="Mat-Nr" breite={160} wert={w.mat_nr} setze={setze('mat_nr')} fehler={fehler}
                  hinweis="Leer lassen: der Artikel bekommt eine eigene Nummer (MDS-0001)." hoechstens={20} />
          )}
          {schonDa && (
            <div className="feldfehler" style={{ alignSelf: 'center' }}>
              {'Die Mat-Nr gibt es schon: '}{schonDa.kurztext}
              {oeffne && <> <button type="button" className="knopf klein" onClick={() => oeffne(schonDa.id)}>öffnen</button></>}
            </div>
          )}
          <Text name="kurztext" titel="Bezeichnung" pflicht breite={mitNummer ? 440 : 620} wert={w.kurztext}
                setze={setze('kurztext')} fehler={fehler} hoechstens={200} />
        </div>
        <div className="feldreihe">
          <Text name="einheit" titel="Lagereinheit" pflicht breite={140} wert={w.einheit} setze={setze('einheit')}
                fehler={fehler} hinweis="ST, PAK, SA, Trommel …" hoechstens={20} vorschlaege={werte.einheit} />
          <Text name="inhalt_je_einheit" titel="Stück je Einheit" breite={140} wert={w.inhalt_je_einheit}
                setze={setze('inhalt_je_einheit')} fehler={fehler} hinweis="Ein Pack mit 10: 10" />
          <Text name="mindestbestand" titel="Mindestbestand" breite={140} wert={w.mindestbestand}
                setze={setze('mindestbestand')} fehler={fehler} hinweis="Darunter: nachbestellen" />
          <Text name="nachbestellfaktor" titel="Faktor" breite={120} wert={w.nachbestellfaktor}
                setze={setze('nachbestellfaktor')} fehler={fehler} hinweis="Verbrauch 30 T mal Faktor" />
        </div>
        <div className="feldreihe">
          <Text name="kategorie" titel="Kategorie" breite={220} wert={w.kategorie} setze={setze('kategorie')}
                fehler={fehler} hoechstens={200} vorschlaege={werte.kategorie} />
          <Text name="bme" titel="Bestelleinheit" breite={140} wert={w.bme} setze={setze('bme')} fehler={fehler}
                hoechstens={20} vorschlaege={werte.bme} />
          <Text name="vpe" titel="Verpackung" breite={220} wert={w.vpe} setze={setze('vpe')} fehler={fehler}
                hoechstens={200} vorschlaege={werte.vpe} />
        </div>
        <Text name="beschreibung" titel="Beschreibung" breite="100%" mehrzeilig wert={w.beschreibung}
              setze={setze('beschreibung')} fehler={fehler} hoechstens={4000} />
        <Text name="bemerkung" titel="Bemerkung" breite="100%" wert={w.bemerkung} setze={setze('bemerkung')}
              fehler={fehler} hoechstens={4000} />
        <div className="feldreihe" style={{ alignItems: 'center' }}>
          <Haken name="standard" titel="Standardartikel" wert={!!w.standard} setze={setze('standard')} />
          {w.standard && (
            <Text name="standard_menge" titel="Typische Menge" breite={140} wert={w.standard_menge}
                  setze={setze('standard_menge')} fehler={fehler} />
          )}
          {!mitNummer && (
            <Haken name="aktiv" titel="aktiv" wert={w.aktiv !== false} setze={setze('aktiv')}
                   hinweis="Ausgesetzt: bleibt mit allen Bewegungen stehen, wird aber nicht mehr vorgeschlagen." />
          )}
        </div>
        {mitNummer && (
          <>
            <h3 className="abschnitt">Bilder</h3>
            <Abwurf klein accept=".jpg,.jpeg,.png,.webp,image/*" laeuft={laeuft}
              laufText="Bild wird hinterlegt …" titel="Bild hier ablegen"
              hinweis="JPEG, PNG oder WebP. Mehrere auf einmal gehen. Sie werden mit dem Artikel gespeichert."
              nimm={(st) => setBilder([...bilder, ...st.map((x) => x.datei)])} />
            {!!bilder.length && (
              <div className="matbilder" style={{ marginTop: 8 }}>
                {bilder.map((b, i) => (
                  <div key={i} className="matbildrahmen">
                    <img className="matbild" alt="" src={URL.createObjectURL(b)} />
                    <button type="button" className="knopf klein" disabled={laeuft}
                            onClick={() => setBilder(bilder.filter((_, j) => j !== i))}>entfernen</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div className="reihe" style={{ marginTop: 14, gap: 8 }}>
          <button className="knopf haupt" disabled={laeuft || !!schonDa}>{laeuft ? 'Einen Moment' : 'Speichern'}</button>
          <button type="button" className="knopf" disabled={laeuft} onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}

// ============================================================================
// Bestellungen
// ============================================================================

const ZUSTAND: Record<string, { text: string; klasse: string }> = {
  entwurf: { text: 'Entwurf', klasse: 'merker blau' },
  versendet: { text: 'versendet', klasse: 'merker warnung' },
  erledigt: { text: 'erledigt', klasse: 'merker gruen' },
  storniert: { text: 'storniert', klasse: 'merker leise' },
};

/* BESTELLUNGEN (.251): WARENKORB, VORSCHLÄGE, KARTEN.
 * ============================================================================
 * ORIGINALTEXT MARVIN (28.09.2026)
 *   „das bestellvormular ist nicht gut und übersichtlich, oben sind die
 *    standarddaten danach mit livesuche eine saubere auflisteung. auch bei
 *    den bestellungen sollen attraktiver und innovativer darfgestellt
 *    werden."
 *   „wo ist der warenkorb und die vorgeschlagenen bestellungen? wo trage ich
 *    das lieferdatum ein?"
 *
 * Oben drei Kacheln: Warenkorb, unterwegs, nachbestellen. Darunter die
 * Vorschläge mit Menge und Einheit, ein Klick legt sie in den Warenkorb.
 * Dann die Bestellungen als Karten mit Bildern und Lieferstand. Der
 * Warenkorb ist der eine Entwurf. */
function Bestellungen({ ich, oeffne }: { ich: Anmelder; oeffne: (id: string) => void }) {
  const [d, setD] = useState<{ bestellungen: any[]; einstellungen: Record<string, string | null> } | null>(null);
  const [vor, setVor] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [filter, setFilter] = useState<'offen' | 'alle'>('offen');
  const [mengen, setMengen] = useState<Record<string, { menge: string; einheit: string }>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const bestellen = darf(ich, 'material.bestellen');
  async function laden() {
    try {
      const [b, v] = await Promise.all([api.materialBestellungen(), api.materialVorschlaege()]);
      setD(b); setVor(v); setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const alle = d?.bestellungen ?? [];
  const korb = alle.find((b) => b.zustand === 'entwurf') ?? null;
  const unterwegs = alle.filter((b) => b.zustand === 'versendet');
  const offenePos = unterwegs.reduce((n, b) => n + (b.offen ?? 0), 0);
  const naechster = unterwegs.map((b) => b.naechster_termin).filter(Boolean).sort()[0] ?? null;
  const ohneTermin = unterwegs.filter((b) => !b.naechster_termin).length;
  const sicht = alle.filter((b) => b.zustand !== 'entwurf' && (filter === 'alle' || b.zustand === 'versendet'));
  const zuBestellen = (vor ?? []).filter((v) => v.menge > 0);
  const mengeVon = (v: any) => mengen[v.artikel_id] ?? { menge: String(v.menge || ''), einheit: v.einheit };

  async function inDenKorb(liste: any[]) {
    setLaeuft(true); setFehler(null); setMeldung(null);
    try {
      const r = await api.materialInDenWarenkorb(liste.map((v) => ({ artikel_id: v.artikel_id, menge: mengeVon(v).menge, einheit: mengeVon(v).einheit })));
      setMeldung(`${liste.length === 1 ? liste[0].kurztext + ' liegt' : liste.length + ' Artikel liegen'} im Warenkorb (Bestellung ${r.nummer}).`);
      setMengen({});
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  async function korbOeffnen() {
    if (korb) { setOffen(korb.id); return; }
    try { const r = await api.materialBestellungNeu('leer'); await laden(); setOffen(r.id); } catch (f) { setFehler(ausFehler(f)); }
  }

  return (
    <>
      <Fehlerkasten lage={fehler} />
      {meldung && <div className="hinweis">✅ {meldung}</div>}
      <div className="matkacheln">
        <button type="button" className={korb?.positionen ? 'matkachel korb voll' : 'matkachel korb'} onClick={korbOeffnen} disabled={!bestellen && !korb}>
          <span className="kachelkopf">Warenkorb</span>
          <span className="kachelzahl">{korb ? korb.positionen : 0}</span>
          <span className="kachelzeile">{korb ? (korb.positionen === 1 ? 'Position, noch nicht versendet' : 'Positionen, noch nicht versendet') : 'leer'}</span>
          <span className="kachelaktion">{korb ? 'öffnen und senden ›' : bestellen ? 'neu anlegen ›' : ''}</span>
        </button>
        <div className="matkachel">
          <span className="kachelkopf">Unterwegs</span>
          <span className="kachelzahl">{offenePos}</span>
          <span className="kachelzeile">{offenePos === 1 ? 'Position' : 'Positionen'} in {unterwegs.length} {unterwegs.length === 1 ? 'Bestellung' : 'Bestellungen'}</span>
          <span className="kachelaktion">
            {naechster ? `nächster Liefertermin ${datum(naechster)}` : unterwegs.length ? 'noch kein Liefertermin' : 'nichts offen'}
            {ohneTermin > 0 && naechster ? `, ${ohneTermin} ohne Termin` : ''}
          </span>
        </div>
        <a className={zuBestellen.length ? 'matkachel warnung' : 'matkachel'} href="#vorschlaege"
           onClick={(e) => { e.preventDefault(); document.getElementById('vorschlaege')?.scrollIntoView({ behavior: 'smooth' }); }}>
          <span className="kachelkopf">Nachbestellen</span>
          <span className="kachelzahl">{zuBestellen.length}</span>
          <span className="kachelzeile">{zuBestellen.length === 1 ? 'Artikel knapp' : 'Artikel knapp'}</span>
          <span className="kachelaktion">{zuBestellen.length ? 'zu den Vorschlägen ›' : 'alles im grünen Bereich'}</span>
        </a>
      </div>

      <div id="vorschlaege" />
      <Karte titel={vor ? `Vorschläge (${zuBestellen.length})` : 'Vorschläge'}
             rechts={bestellen && zuBestellen.length > 1 ? (
               <button className="knopf klein haupt" disabled={laeuft} onClick={() => inDenKorb(zuBestellen)}>Alle in den Warenkorb</button>
             ) : undefined}>
        {vor && !vor.length && <Leer text="Kein Artikel unter dem Mindestbestand, keiner nach Verbrauch knapp." />}
        {!!vor?.length && (
          <div className="vorschlagliste">
            {vor.map((v) => {
              const m = mengeVon(v);
              const anteil = v.mindest ? Math.max(0, Math.min(1, v.bestand / (v.mindest * 2))) : null;
              return (
                <div key={v.artikel_id} className={v.menge > 0 ? 'vorschlag' : 'vorschlag gedeckt'}>
                  <ZoomBild id={v.bild_id} titel={v.kurztext} />
                  <div className="vorschlag-text anklickbar" onClick={() => oeffne(v.artikel_id)}>
                    <div><b>{v.mat_nr}</b> {v.kurztext}</div>
                    <div className="klein leise">
                      <span className={v.lage === 'minus' ? 'merker fehler' : v.lage === 'nachbestellen' ? 'merker warnung' : 'merker blau'}>{v.grund}</span>
                      {' '}Bestand {menge(v.bestand)} {v.einheit}{v.mindest !== null ? `, Mindest ${menge(v.mindest)}` : ''}
                      {v.verbrauch_30 ? `, ${menge(v.verbrauch_30)} in 30 Tagen` : ''}
                      {v.unterwegs ? `, ${menge(v.unterwegs)} unterwegs` : ''}
                      {v.im_warenkorb ? `, ${menge(v.im_warenkorb)} im Warenkorb` : ''}
                    </div>
                    {anteil !== null && <div className="fuellstand"><span style={{ width: `${Math.round(anteil * 100)}%` }} className={v.lage === 'ok' ? '' : 'knapp'} /></div>}
                  </div>
                  {bestellen && (v.menge > 0 ? (
                    <div className="vorschlag-korb">
                      <input aria-label="Menge" className="mengenfeld" value={m.menge}
                             onChange={(e) => setMengen({ ...mengen, [v.artikel_id]: { ...m, menge: e.target.value } })} />
                      <select aria-label="Einheit" value={m.einheit}
                              onChange={(e) => setMengen({ ...mengen, [v.artikel_id]: { ...m, einheit: e.target.value } })}>
                        {einheitenFuer(v, 'bestellung').map((x) => <option key={x.wert} value={x.wert}>{x.text}</option>)}
                      </select>
                      <button className="knopf klein haupt" disabled={laeuft} onClick={() => inDenKorb([v])}>In den Warenkorb</button>
                    </div>
                  ) : <span className="merker gruen">gedeckt</span>)}
                </div>
              );
            })}
          </div>
        )}
      </Karte>

      <Karte titel={`Bestellungen bei der Telekom (${sicht.length})`}
             rechts={(
               <div className="reihe" style={{ gap: 8 }}>
                 <div className="umschalter" role="tablist">
                   <button aria-pressed={filter === 'offen'} onClick={() => setFilter('offen')}>unterwegs</button>
                   <button aria-pressed={filter === 'alle'} onClick={() => setFilter('alle')}>alle</button>
                 </div>
                 <button className="knopf klein" onClick={() => extern(api.materialOffenXlsxAdresse())}>Offene als Excel</button>
               </div>
             )}>
        {d && !sicht.length && <Leer text={filter === 'offen' ? 'Nichts unterwegs. Alles geliefert.' : 'Noch keine Bestellung.'} />}
        <div className="bestellkarten">
          {sicht.map((b) => {
            const aktiv = (b.geliefert ?? 0) + (b.offen ?? 0);
            const anteil = aktiv ? (b.geliefert ?? 0) / aktiv : 1;
            return (
              <div key={b.id} className={`bestellkarte ${b.zustand}`} role="button" tabIndex={0}
                   onClick={() => setOffen(b.id)} onKeyDown={(e) => e.key === 'Enter' && setOffen(b.id)}>
                <div className="bestellkarte-kopf">
                  <span className="bestellnummer">Nr. {b.nummer}</span>
                  <span className={ZUSTAND[b.zustand]?.klasse ?? 'merker'}>{ZUSTAND[b.zustand]?.text ?? b.zustand}</span>
                  <span className="klein leise" style={{ marginLeft: 'auto' }}>{datum(b.datum)}</span>
                </div>
                <div className="bestellkarte-bilder">
                  {(b.vorschau ?? []).map((x: any, i: number) => <Bild key={i} id={x.bild_id} />)}
                  {(b.positionen ?? 0) > 4 && <span className="mehr">+{b.positionen - 4}</span>}
                </div>
                <div className="klein zweizeilig">{b.inhalt}</div>
                <div className="lieferstand">
                  <div className="fuellstand"><span style={{ width: `${Math.round(anteil * 100)}%` }} /></div>
                  <span className="klein">{b.geliefert ?? 0} von {aktiv} geliefert</span>
                </div>
                <div className="klein leise">
                  {b.zustand === 'versendet'
                    ? (b.naechster_termin ? `Liefertermin ${datum(b.naechster_termin)}` : 'noch kein Liefertermin')
                    : b.status ?? ''}
                  {b.sd_nr ? `, SD ${b.sd_nr}` : ''}{b.sm_nr ? `, SM ${b.sm_nr}` : ''}
                </div>
                {b.hat_pdf && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <button className="knopf klein" onClick={() => extern(api.materialBestellungPdfAdresse(b.id))}>PDF</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Karte>
      {offen && (
        <BestellungFenster id={offen} ich={ich} einstellungen={d?.einstellungen ?? {}}
                           zu={() => { setOffen(null); laden(); }} oeffne={(id) => { setOffen(null); oeffne(id); }} />
      )}
    </>
  );
}

const tagIso = (z: string | null | undefined) => z ? new Date(z).toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }) : '';

function BestellungFenster({ id, ich, zu, oeffne, einstellungen }: {
  id: string; ich: Anmelder; zu: () => void; oeffne: (id: string) => void; einstellungen: Record<string, string | null>;
}) {
  const [b, setB] = useState<any>(null);
  const [liste, setListe] = useState<any[]>([]);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [kopf, setKopf] = useState<any>({});
  const [neuA, setNeuA] = useState<any | null>(null);
  const [neu, setNeu] = useState({ menge: '', einheit: '', trommeln: '' });
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [fuerAlle, setFuerAlle] = useState('');
  const [zoom, setZoom] = useState<any | null>(null);
  const bestellen = darf(ich, 'material.bestellen');
  const e = einstellungen;
  async function laden() {
    try {
      const r = await api.materialBestellung(id);
      setB(r);
      setKopf({ betreff: r.betreff ?? '', wunschtermin: r.wunschtermin ?? '', baustelle: r.baustelle ?? '',
                sd_nr: r.sd_nr ?? '', sm_nr: r.sm_nr ?? '', status: r.status ?? '', anmerkung: r.anmerkung ?? '' });
      setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); if (bestellen) api.materialAuswahl().then(setListe).catch(() => {}); }, [id]);
  useFrisch(laden);
  async function tu(f: () => Promise<unknown>, nachher?: string) {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try { await f(); await laden(); if (nachher) setHinweis(nachher); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  const entwurf = b?.zustand === 'entwurf';
  const offen = b && (b.zustand === 'versendet' || b.zustand === 'erledigt');
  const kopfSpeichern = (k: string) => {
    if (!b || (kopf[k] ?? '') === (b[k] ?? '')) return;
    tu(() => api.materialBestellungAendern(id, { [k]: kopf[k] }));
  };
  const kopfFeld = (k: string, titel: string, breite: number, platz?: string) => (
    <div className="feld" style={{ width: breite }}>
      <label htmlFor={`k_${k}`}>{titel}</label>
      <input id={`k_${k}`} value={kopf[k] ?? ''} placeholder={platz} disabled={!bestellen}
             onChange={(ev) => setKopf({ ...kopf, [k]: ev.target.value })} onBlur={() => kopfSpeichern(k)} />
    </div>
  );
  async function hinzu() {
    if (!neuA) return;
    await tu(async () => {
      await api.materialPositionNeu(id, { artikel_id: neuA.id, menge: neu.menge || undefined,
                                          einheit: neu.einheit || neuA.einheit, trommeln: neu.trommeln || undefined });
      setNeuA(null); setNeu({ menge: '', einheit: '', trommeln: '' });
    });
  }
  const aktiv = (b?.positionen ?? []).filter((p: any) => !p.storniert);

  return (
    <Dialog titel={b ? (entwurf ? `Warenkorb, Bestellung ${b.nummer}` : `Bestellung ${b.nummer}`) : 'Bestellung'}
            merker={b ? ZUSTAND[b.zustand]?.text : undefined}
            untertitel={b ? (entwurf ? `angelegt ${datum(b.datum)}` : `${datum(b.datum)}, ${b.betreff ?? ''}`) : undefined}
            breite={1080} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {hinweis && <div className="hinweis">✅ {hinweis}</div>}
      {b && (
        <>
          {/* 1. DIE FESTEN ANGABEN OBEN. Was aus der Konfiguration kommt, steht
                 grau; was zu dieser Bestellung gehört, lässt sich tippen. */}
          <div className="bestellkopf">
            <dl className="bestellkopf-fest">
              <dt>An</dt><dd>Deutsche Telekom<br /><span className="klein leise">{e.telekom_mail ?? 'kein Empfänger eingetragen'}</span></dd>
              <dt>Kreditor</dt><dd>{e.kreditor_nr ?? ''}</dd>
              <dt>Lieferadresse</dt><dd>{e.lieferadresse ?? ''}</dd>
              <dt>Ansprechpartner</dt><dd>{e.ansprechpartner ?? ''}</dd>
              <dt>Kopie</dt><dd>Blindkopie ans Backoffice{e.cc_mail ? `, dazu offen an ${e.cc_mail}` : ''}</dd>
            </dl>
            <div className="bestellkopf-frei">
              {kopfFeld('betreff', 'Betreff', 260, 'Materialbestellung')}
              {kopfFeld('wunschtermin', 'Wunschtermin', 200, 'schnellstmöglich')}
              {kopfFeld('baustelle', 'Baustelle (freiwillig)', 260)}
              {!entwurf && (<>
                {kopfFeld('sd_nr', 'SD-Nr', 150)}
                {kopfFeld('sm_nr', 'SM-Nr', 150)}
                {kopfFeld('status', 'Notiz zum Stand', 300)}
              </>)}
              {!entwurf && b.versendet_am && <p className="klein leise" style={{ width: '100%', margin: 0 }}>Versendet {zeitpunkt(b.versendet_am)}{b.kanal ? `, ${b.kanal}` : ''}</p>}
            </div>
          </div>

          {/* 2. DIE LIVESUCHE. */}
          {entwurf && bestellen && (
            <form className="bestellsuche" onSubmit={(ev) => { ev.preventDefault(); if (!laeuft) hinzu(); }}>
              <ArtikelWahl liste={liste} wert={neuA} name="neu_artikel" titel="Artikel hinzufügen" pflicht={false}
                           platzhalter="Mat-Nr oder Bezeichnung tippen, z. B. SNR 7 oder 40250914"
                           setze={(a) => { setNeuA(a); setNeu({ menge: '', einheit: '', trommeln: '' }); }} />
              {neuA && (
                <div className="feldreihe" style={{ alignItems: 'flex-end' }}>
                  <Text name="neu_menge" titel="Menge" breite={110} wert={neu.menge} setze={(v) => setNeu({ ...neu, menge: v })}
                        platzhalter={Number(neuA.standard_menge) > 0 && neuA.bme === neuA.einheit ? menge(Number(neuA.standard_menge)) : '1'} />
                  <EinheitWahl artikel={neuA} zweck="bestellung" wert={neu.einheit} setze={(v) => setNeu({ ...neu, einheit: v })} />
                  <Text name="neu_trommeln" titel="Bemerkung / Trommeln" breite={200} wert={neu.trommeln}
                        platzhalter="z. B. 2x3000 M" setze={(v) => setNeu({ ...neu, trommeln: v })} />
                  <button className="knopf haupt" disabled={laeuft}>Hinzufügen</button>
                </div>
              )}
            </form>
          )}

          {/* 3. DIE LISTE. */}
          <h3 className="abschnitt">{entwurf ? `Im Warenkorb (${aktiv.length})` : `Positionen (${aktiv.length})`}</h3>
          {!entwurf && bestellen && b.zustand === 'versendet' && (
            <div className="reihe liefertermin-alle">
              <label htmlFor="fuer_alle" className="klein"><b>Liefertermin</b> für alle offenen Positionen</label>
              <input id="fuer_alle" type="date" value={fuerAlle} onChange={(ev) => setFuerAlle(ev.target.value)} />
              <button className="knopf klein" disabled={laeuft || !fuerAlle}
                      onClick={() => tu(() => api.materialLiefertermin(id, fuerAlle), 'Liefertermin eingetragen. Der Bot erinnert 2 Tage vorher.')}>eintragen</button>
              <span className="klein leise">oder je Position rechts in der Liste</span>
            </div>
          )}
          {!b.positionen.length ? (
            <Leer text={entwurf ? 'Noch leer. Oben einen Artikel suchen, oder unter Vorschläge „In den Warenkorb".' : 'Keine Position.'} />
          ) : (
            <div className="rollt">
              <table className="liste bestellliste">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>Pos.</th>
                    <th style={{ width: 52 }}></th>
                    <th>Artikel</th>
                    <th className="zahl" style={{ width: 170 }}>Menge</th>
                    <th style={{ width: entwurf ? 190 : 160 }}>{entwurf ? 'Bemerkung / Trommeln' : 'Liefertermin'}</th>
                    <th style={{ width: entwurf ? 44 : 200 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {b.positionen.map((p: any) => (
                    <tr key={p.id} style={{ opacity: p.storniert ? 0.5 : 1 }}>
                      <td className="klein leise">{p.pos}</td>
                      <td><button type="button" className="bildknopf" onClick={() => p.bild_id && setZoom(p)} aria-label="Bild vergrößern"><Bild id={p.bild_id} /></button></td>
                      <td className={p.artikel_id ? 'anklickbar' : undefined} onClick={() => p.artikel_id && oeffne(p.artikel_id)}>
                        <div><b>{p.mat_nr ?? ''}</b> {p.kurztext ?? p.artikel_text}</div>
                        <div className="klein leise">
                          {p.bestand !== null && p.bestand !== undefined ? `Bestand ${menge(Number(p.bestand))} ${p.lager_einheit ?? ''}` : ''}
                          {p.mindestbestand !== null && p.mindestbestand !== undefined ? `, Mindest ${menge(Number(p.mindestbestand))}` : ''}
                          {!entwurf && p.bemerkung ? `, ${p.bemerkung}` : ''}{!entwurf && p.trommeln ? `, ${p.trommeln}` : ''}
                        </div>
                      </td>
                      <td className="zahl">
                        {entwurf && bestellen ? (
                          <div className="mengeeinheit">
                            <input className="mengenfeld" defaultValue={String(p.menge ?? '')} aria-label="Menge"
                                   onBlur={(ev) => ev.target.value !== String(p.menge ?? '') && tu(() => api.materialPositionAendern(p.id, { menge: ev.target.value }))} />
                            <select aria-label="Einheit" value={p.einheit ?? ''}
                                    onChange={(ev) => tu(() => api.materialPositionAendern(p.id, { einheit: ev.target.value }))}>
                              {[...einheitenFuer({ einheit: p.lager_einheit ?? p.einheit, inhalt_je_einheit: p.inhalt_je_einheit, bme: p.bme }, 'bestellung'),
                                ...(p.einheit && ![p.lager_einheit, 'ST', p.bme].includes(p.einheit) ? [{ wert: p.einheit, text: p.einheit }] : [])]
                                .map((x) => <option key={x.wert} value={x.wert}>{x.text}</option>)}
                            </select>
                          </div>
                        ) : <b>{menge(p.menge === null ? null : Number(p.menge))} {p.einheit ?? ''}</b>}
                      </td>
                      <td className="klein">
                        {entwurf && bestellen ? (
                          <input style={{ width: '100%' }} defaultValue={[p.bemerkung, p.trommeln].filter(Boolean).join(', ')} placeholder="z. B. 2x3000 M" aria-label="Bemerkung"
                                 onBlur={(ev) => ev.target.value !== [p.bemerkung, p.trommeln].filter(Boolean).join(', ')
                                   && tu(() => api.materialPositionAendern(p.id, { trommeln: ev.target.value, bemerkung: null }))} />
                        ) : bestellen && offen && !p.storniert && !p.erledigt ? (
                          <input type="date" defaultValue={tagIso(p.zugesagt_am)} aria-label="Liefertermin"
                                 onChange={(ev) => tu(() => api.materialPositionAendern(p.id, { zugesagt_am: ev.target.value || null }))} />
                        ) : p.zugesagt_am ? datum(p.zugesagt_am) : ''}
                      </td>
                      <td>
                        {entwurf ? (bestellen && (
                          <button className="knopf klein" title="aus dem Warenkorb nehmen" aria-label="entfernen" disabled={laeuft}
                                  onClick={() => tu(() => api.materialPositionWeg(p.id))}>✕</button>
                        )) : (
                          <div className="reihe" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {p.storniert ? <span className="merker leise">storniert</span>
                              : p.erledigt ? <span className="merker gruen">geliefert</span>
                              : <span className="merker warnung">offen</span>}
                            {bestellen && !p.storniert && (
                              <button className="knopf klein" disabled={laeuft}
                                      onClick={() => tu(() => api.materialPositionAendern(p.id, { erledigt: !p.erledigt }))}>
                                {p.erledigt ? 'wieder offen' : 'geliefert'}
                              </button>
                            )}
                            {bestellen && !p.erledigt && (
                              <button className="knopf klein" disabled={laeuft}
                                      onClick={() => tu(() => api.materialPositionAendern(p.id, { storniert: !p.storniert }))}>
                                {p.storniert ? 'zurück' : 'stornieren'}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {b.anmerkung && !bestellen && <p className="klein" style={{ whiteSpace: 'pre-wrap' }}>{b.anmerkung}</p>}

          {/* 4. DIE KNÖPFE. */}
          <div className="bestellfuss">
            {entwurf && bestellen && (<>
              <button className="knopf haupt" disabled={laeuft || !aktiv.length}
                      onClick={() => tu(() => api.materialBestellungSenden(id), 'An die Telekom gesendet. Die Blindkopie liegt im Backoffice.')}>An die Telekom senden</button>
              <button className="knopf" disabled={!aktiv.length} onClick={() => extern(api.materialBestellungVorschauAdresse(id))}>PDF ansehen</button>
              <button className="knopf" disabled={laeuft} onClick={async () => {
                setLaeuft(true);
                try { await api.materialBestellungVerwerfen(id); zu(); } catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
              }}>Warenkorb leeren</button>
              <span className="klein leise" style={{ marginLeft: 'auto' }}>
                {aktiv.length} {aktiv.length === 1 ? 'Position' : 'Positionen'}, geht als Mail mit PDF an {e.telekom_mail ?? 'die Telekom'}
              </span>
            </>)}
            {!entwurf && b.hat_pdf && (
              <button className="knopf" onClick={() => extern(api.materialBestellungPdfAdresse(b.id))}>PDF ansehen</button>
            )}
            {bestellen && b.zustand === 'versendet' && (
              <button className="knopf" disabled={laeuft} onClick={() => tu(() => api.materialBestellungAendern(id, { zustand: 'erledigt' }), 'Als ganz geliefert markiert.')}>Alles geliefert</button>
            )}
            {bestellen && (b.zustand === 'versendet' || b.zustand === 'erledigt') && (
              <button className="knopf" disabled={laeuft} onClick={() => tu(() => api.materialBestellungAendern(id, { zustand: 'storniert' }), 'Storniert.')}>Bestellung stornieren</button>
            )}
          </div>
          {zoom && <Bildzoom titel={zoom.kurztext ?? ''} adressen={[api.materialBildAdresse(zoom.bild_id)]} zu={() => setZoom(null)} />}
        </>
      )}
    </Dialog>
  );
}

// ============================================================================
// Die Bewegungen
// ============================================================================

function Wareneingang({ ich, oeffne }: { ich: Anmelder; oeffne: (id: string) => void }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [suche, setSuche] = useState('');
  const [neu, setNeu] = useState(false);
  const buchen = darf(ich, 'material.buchen');
  async function laden() {
    try { setZeilen(await api.materialWareneingang()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const sicht = (zeilen ?? []).filter((z) => passt({ ...z, kurztext: z.lieferschein_nr }, suche));
  const scheine = new Set((zeilen ?? []).map((z) => z.lieferschein_nr)).size;
  async function storno(nummer: string) {
    const grund = await eingeben(`Lieferschein ${nummer} stornieren. Warum?`);
    if (grund === null) return;
    try { await api.materialWareneingangStorno(nummer, grund); await laden(); } catch (f) { setFehler(ausFehler(f)); }
  }
  let letzter = '';
  return (
    <>
      <Karte titel={zeilen ? `Wareneingang (${scheine} Lieferscheine, ${zeilen.length} Positionen)` : 'Wareneingang'}
             rechts={(
               <div className="reihe" style={{ gap: 8 }}>
                 <input type="search" autoComplete="off" placeholder="Lieferschein, Mat-Nr, Text"
                        value={suche} onChange={(e) => setSuche(e.target.value)} />
                 {buchen && <button className="knopf klein haupt" onClick={() => setNeu(true)}>Wareneingang buchen</button>}
               </div>
             )}>
        <Fehlerkasten lage={fehler} />
        {zeilen && !zeilen.length && <Leer text="Noch kein Wareneingang." />}
        {!!sicht.length && (
          <div className="rollt">
            <table className="liste">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Lieferschein</th>
                  <th style={{ width: 70 }}>Pos.</th>
                  <th style={{ width: 96 }}>Datum</th>
                  <th style={{ width: 96 }}>Mat-Nr</th>
                  <th>Bezeichnung</th>
                  <th className="zahl" style={{ width: 120 }}>Menge</th>
                  <th className="zahl" style={{ width: 120 }}>im Lager</th>
                  <th style={{ width: 150 }}>gebucht</th>
                </tr>
              </thead>
              <tbody>
                {sicht.map((z) => {
                  const erste = z.lieferschein_nr !== letzter;
                  letzter = z.lieferschein_nr;
                  return (
                    <tr key={z.id} className={z.artikel_id ? 'anklickbar' : undefined}
                        onClick={() => z.artikel_id && oeffne(z.artikel_id)} style={{ opacity: z.storniert_am ? 0.45 : 1 }}>
                      <td>{erste ? z.lieferschein_nr : ''}
                        {erste && buchen && !z.storniert_am && (
                          <div><button className="knopf klein" onClick={(ev) => { ev.stopPropagation(); storno(z.lieferschein_nr); }}>stornieren</button></div>
                        )}
                      </td>
                      <td className="klein">{z.pos}</td>
                      <td>{datum(z.datum)}</td>
                      <td className="klein">{z.mat_nr ?? ''}</td>
                      <td>{z.bezeichnung}{z.storniert_am && <span className="merker leise" style={{ marginLeft: 6 }}>storniert</span>}</td>
                      <td className="zahl">{menge(Number(z.menge), z.einheit ?? '')}</td>
                      <td className="zahl">{menge(Number(z.menge_lager), z.lager_einheit ?? '')}</td>
                      <td className="klein leise">{zeitpunkt(z.gebucht_am)}{z.quelle !== 'uebernahme' ? `, ${QUELLE[z.quelle] ?? z.quelle}` : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
      {neu && <WareneingangFenster zu={() => setNeu(false)} />}
    </>
  );
}

function WareneingangFenster({ zu }: { zu: () => void }) {
  const [liste, setListe] = useState<any[]>([]);
  const [kopf, setKopf] = useState({ lieferschein_nr: '', datum: heute() });
  const [pos, setPos] = useState<any[]>([]);
  const [wahl, setWahl] = useState<any | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<any | null>(null);
  useEffect(() => { api.materialAuswahl().then(setListe).catch((f) => setFehler(ausFehler(f))); }, []);
  const nachId = (id: string | null) => liste.find((a) => a.id === id) ?? null;
  async function lesen(d: File) {
    setLaeuft(true); setFehler(null);
    try {
      const r = await api.materialLieferscheinLesen(d);
      setKopf({ lieferschein_nr: r.nummer ?? kopf.lieferschein_nr, datum: r.datum ?? kopf.datum });
      setPos(r.positionen.map((p: any) => ({ ...p, menge: String(p.menge), einheit: p.einheit ?? '' })));
      if (!r.positionen.length) setFehler({ text: 'Im Lieferschein ist keine Position zu lesen. Bitte von Hand erfassen.' } as any);
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  async function buchen() {
    setLaeuft(true); setFehler(null);
    try {
      const r = await api.materialWareneingangBuchen({ ...kopf, positionen: pos.map((p) => ({
        pos: p.pos, artikel_id: p.artikel_id, mat_nr: p.mat_nr, text: p.kurztext ?? p.text, menge: p.menge,
        einheit: p.einheit || null, menge_lager: p.menge_lager || null })) });
      setErgebnis(r);
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel="Wareneingang buchen" breite={960} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {ergebnis ? (
        <>
          <div className="hinweis">
            ✅ Lieferschein {ergebnis.lieferschein}: {ergebnis.gebucht} Positionen gebucht
            {ergebnis.schon_da ? `, ${ergebnis.schon_da} waren schon gebucht` : ''}.
            {ergebnis.ohne_artikel.length ? ` Ohne Artikel: ${ergebnis.ohne_artikel.join(', ')}.` : ''}
            {ergebnis.erledigt.length ? ` Bestellung ${ergebnis.erledigt.join(', ')} beliefert.` : ''}
          </div>
          <button className="knopf haupt" onClick={zu}>Schließen</button>
        </>
      ) : (
        <>
          <Abwurf klein accept=".pdf,application/pdf" laeuft={laeuft} laufText="Der Lieferschein wird gelesen …"
            titel="Lieferschein (PDF) hier ablegen" hinweis="Nummer, Datum und Positionen werden vorgeschlagen; prüfen, dann buchen."
            nimm={(st) => st[0] && lesen(st[0].datei)} />
          <div className="feldreihe" style={{ marginTop: 10 }}>
            <Text name="lieferschein_nr" titel="Lieferschein-Nr" pflicht breite={200} wert={kopf.lieferschein_nr}
                  setze={(v) => setKopf({ ...kopf, lieferschein_nr: v })} fehler={fehler} />
            <Text name="datum" titel="Datum" art="date" breite={170} wert={kopf.datum}
                  setze={(v) => setKopf({ ...kopf, datum: v })} fehler={fehler} />
          </div>
          <table className="liste">
            <thead>
              <tr><th>Artikel</th><th className="zahl" style={{ width: 110 }}>Menge</th><th style={{ width: 100 }}>Einheit</th>
                  <th className="zahl" style={{ width: 130 }}>im Lager</th><th style={{ width: 90 }}></th></tr>
            </thead>
            <tbody>
              {pos.map((p, i) => {
                const a = nachId(p.artikel_id);
                const setz = (k: string, v: any) => setPos(pos.map((x, j) => j === i ? { ...x, [k]: v } : x));
                return (
                  <tr key={i}>
                    <td>{a ? <><b>{a.mat_nr}</b> {a.kurztext}</> : <span className="merker fehler">{p.mat_nr ?? ''} unbekannt</span>}</td>
                    <td className="zahl"><input style={{ width: 80, textAlign: 'right' }} value={p.menge} aria-label="Menge" onChange={(e) => setz('menge', e.target.value)} /></td>
                    <td>
                      {a ? (
                        <select aria-label="Einheit" value={p.einheit || a.einheit} onChange={(e) => setz('einheit', e.target.value)}>
                          {[...einheitenFuer(a, 'eingang'), ...(p.einheit && !einheitenFuer(a, 'eingang').some((x) => x.wert === p.einheit) ? [{ wert: p.einheit, text: p.einheit }] : [])]
                            .map((x) => <option key={x.wert} value={x.wert}>{x.text}</option>)}
                        </select>
                      ) : <input style={{ width: 80 }} value={p.einheit} aria-label="Einheit" onChange={(e) => setz('einheit', e.target.value)} />}
                    </td>
                    <td className="zahl"><input style={{ width: 80, textAlign: 'right' }} value={p.menge_lager ?? ''} aria-label="Im Lager"
                         placeholder="gerechnet" onChange={(e) => setz('menge_lager', e.target.value)} /> {a?.einheit ?? ''}</td>
                    <td><button className="knopf klein" onClick={() => setPos(pos.filter((_, j) => j !== i))}>entfernen</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="matneu">
            <ArtikelWahl liste={liste} wert={wahl} name="we_artikel" setze={(a) => {
              if (a) setPos([...pos, { artikel_id: a.id, mat_nr: a.mat_nr, kurztext: a.kurztext, menge: '', einheit: a.einheit }]);
              setWahl(null);
            }} />
          </div>
          <p className="klein leise">„im Lager" leer lassen: die Plattform rechnet (Meter auf der Trommel sind eine Trommel, Stück bei Packs werden geteilt).</p>
          <div className="reihe" style={{ gap: 8, marginTop: 10 }}>
            <button className="knopf haupt" disabled={laeuft || !pos.length || !kopf.lieferschein_nr} onClick={buchen}>Buchen</button>
            <button className="knopf" disabled={laeuft} onClick={zu}>Abbrechen</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

function Entnahmen({ ich, oeffne }: { ich: Anmelder; oeffne?: (id: string) => void }) {
  const [d, setD] = useState<{ entnahmen: any[]; firmen: any[]; eigene_firma: string | null } | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [suche, setSuche] = useState('');
  const [firma, setFirma] = useState('');
  const buchen = darf(ich, 'material.buchen') && !ich.istSub;
  async function laden() {
    try { setD(await api.materialEntnahmen()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  async function storno(id: string) {
    const grund = await eingeben('Diese Entnahme stornieren. Warum?');
    if (grund === null) return;
    try { await api.materialEntnahmeStorno(id, grund); await laden(); } catch (f) { setFehler(ausFehler(f)); }
  }
  const firmen = [...new Set((d?.entnahmen ?? []).map((e) => e.firma ?? 'ohne Firma'))].sort();
  const sicht = (d?.entnahmen ?? []).filter((e) => passt(e, suche) && (!firma || (e.firma ?? 'ohne Firma') === firma));
  return (
    <>
      <Fehlerkasten lage={fehler} />
      <Karte titel={d ? `Entnahmen (${d.entnahmen.length})` : 'Entnahmen'}
             rechts={<input type="search" autoComplete="off" placeholder="Mat-Nr, Text"
                            value={suche} onChange={(e) => setSuche(e.target.value)} />}>
        {!d?.eigene_firma && (
          <div className="reihe" style={{ marginBottom: 10 }}>
            <select value={firma} onChange={(e) => setFirma(e.target.value)} aria-label="Firma">
              <option value="">alle Firmen</option>
              {firmen.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        )}
        {d && !d.entnahmen.length && <Leer text="Noch keine Entnahme." />}
        {!!sicht.length && (
          <div className="rollt">
            <table className="liste">
              <thead>
                <tr>
                  <th style={{ width: 96 }}>Datum</th>
                  {!d?.eigene_firma && <th style={{ width: 200 }}>Firma</th>}
                  <th style={{ width: 96 }}>Mat-Nr</th>
                  <th>Bezeichnung</th>
                  <th className="zahl" style={{ width: 110 }}>Menge</th>
                  <th className="zahl" style={{ width: 80 }}>Stück</th>
                  <th style={{ width: 160 }}>Weg</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {sicht.map((e) => (
                  <tr key={e.id} className={e.artikel_id && oeffne ? 'anklickbar' : undefined}
                      onClick={() => e.artikel_id && oeffne?.(e.artikel_id)} style={{ opacity: e.storniert_am ? 0.45 : 1 }}>
                    <td>{datum(e.zeit)}</td>
                    {!d?.eigene_firma && (
                      <td>{e.firma ?? <span className="leise">ohne Firma</span>}
                        {!e.subunternehmer_id && e.firma && <div className="klein leise">keiner Firma zugeordnet</div>}</td>
                    )}
                    <td className="klein">{e.mat_nr ?? ''}</td>
                    <td>{e.bezeichnung}
                      {e.storniert_am && <div className="klein leise">storniert{e.storno_grund ? `: ${e.storno_grund}` : ''}</div>}</td>
                    <td className="zahl">{menge(Number(e.menge), e.einheit ?? '')}</td>
                    <td className="zahl">{e.menge_stueck !== null ? menge(Number(e.menge_stueck)) : ''}</td>
                    <td className="klein">{QUELLE[e.quelle] ?? e.quelle}{e.gemeldet_von ? `, ${e.gemeldet_von}` : ''}</td>
                    <td onClick={(ev) => ev.stopPropagation()}>
                      <div className="reihe" style={{ gap: 6 }}>
                        {e.hat_foto && <button className="knopf klein" onClick={() => extern(api.materialEntnahmeFotoAdresse(e.id))}>Foto</button>}
                        {buchen && !e.storniert_am && <button className="knopf klein" onClick={() => storno(e.id)}>stornieren</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
      {!d?.eigene_firma && (
        <Karte titel="Telegram-Gruppen der Firmen">
          {!d?.firmen.length && (
            <Leer text="Noch keine Firma mit Material-Gruppe. Unter Stammdaten, Firma eintragen." />
          )}
          {!!d?.firmen.length && (
            <table className="liste">
              <tbody>
                {d.firmen.map((f) => (
                  <tr key={f.id}>
                    <td>{f.firma}</td>
                    <td className="klein leise">Gruppe {f.material_chat_id}{f.material_thread_id ? `, Thema ${f.material_thread_id}` : ''}</td>
                    <td>{f.material_aktiv ? <span className="merker gruen">aktiv</span> : <span className="merker leise">aus</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Karte>
      )}
    </>
  );
}

function Inventur({ ich, oeffne }: { ich: Anmelder; oeffne: (id: string) => void }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [mitUebernahme, setMitUebernahme] = useState(false);
  const [zaehlen, setZaehlen] = useState(false);
  const buchen = darf(ich, 'material.buchen');
  async function laden() {
    try { setZeilen(await api.materialInventur()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const sicht = (zeilen ?? []).filter((z) => mitUebernahme || z.art !== 'uebernahme');
  if (zaehlen) return <Zaehlliste zu={() => { setZaehlen(false); laden(); }} />;
  return (
    <Karte titel={zeilen ? `Inventur (${sicht.length} Zählungen)` : 'Inventur'}
           rechts={buchen ? <button className="knopf klein haupt" onClick={() => setZaehlen(true)}>Zählen</button> : undefined}>
      <Fehlerkasten lage={fehler} />
      <div className="reihe" style={{ marginBottom: 10 }}>
        <Haken name="uebernahme" titel="auch den Stand der Übernahme zeigen" wert={mitUebernahme} setze={setMitUebernahme} />
      </div>
      {zeilen && !sicht.length && <Leer text="Noch keine Zählung." />}
      {!!sicht.length && (
        <div className="rollt">
          <table className="liste">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Gezählt</th>
                <th style={{ width: 96 }}>Mat-Nr</th>
                <th>Bezeichnung</th>
                <th className="zahl" style={{ width: 90 }}>Soll</th>
                <th className="zahl" style={{ width: 90 }}>Ist</th>
                <th className="zahl" style={{ width: 100 }}>Differenz</th>
                <th style={{ width: 200 }}>Wer</th>
              </tr>
            </thead>
            <tbody>
              {sicht.map((z) => (
                <tr key={z.id} className="anklickbar" onClick={() => oeffne(z.artikel_id)}>
                  <td className="klein">{zeitpunkt(z.zeit)}</td>
                  <td className="klein">{z.mat_nr}</td>
                  <td>{z.kurztext}{z.art === 'uebernahme' && <span className="merker leise" style={{ marginLeft: 6 }}>Übernahme</span>}</td>
                  <td className="zahl">{z.soll !== null ? menge(Number(z.soll)) : ''}</td>
                  <td className="zahl"><b>{menge(Number(z.ist))}</b> {z.einheit ?? ''}</td>
                  <td className="zahl">{z.differenz !== null && Number(z.differenz) !== 0
                    ? <span className={Number(z.differenz) < 0 ? 'merker fehler' : 'merker gruen'}>{Number(z.differenz) > 0 ? '+' : ''}{menge(Number(z.differenz))}</span>
                    : ''}</td>
                  <td className="klein leise">{z.zaehler ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Karte>
  );
}

/** Die Zählliste: das Gezählte eintragen, nur Geändertes wird gebucht. */
function Zaehlliste({ zu }: { zu: () => void }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [ist, setIst] = useState<Record<string, string>>({});
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState<string | null>(null);
  useEffect(() => { api.materialZaehlliste().then(setZeilen).catch((f) => setFehler(ausFehler(f))); }, []);
  const eingetragen = Object.entries(ist).filter(([, v]) => v.trim() !== '');
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      const r = await api.materialInventurBuchen(eingetragen.map(([artikel_id, v]) => ({ artikel_id, ist: v })));
      setFertig(`${r.gezaehlt} Artikel gezählt und gebucht.`);
      setIst({});
      setZeilen(await api.materialZaehlliste());
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  const sicht = (zeilen ?? []).filter((z) => passt(z, suche));
  return (
    <Karte titel="Zählen"
           rechts={(
             <div className="reihe" style={{ gap: 8 }}>
               <input type="search" autoComplete="off" placeholder="Mat-Nr, Text, Kategorie" value={suche} onChange={(e) => setSuche(e.target.value)} />
               <button className="knopf klein" onClick={zu}>zurück</button>
             </div>
           )}>
      <Fehlerkasten lage={fehler} />
      {fertig && <div className="hinweis">✅ {fertig}</div>}
      <p className="klein leise" style={{ marginTop: 0 }}>
        Nur eintragen, was gezählt wurde. Das Gezählte ist ab dann die Grundlage des Bestands; leere Felder bleiben, wie sie sind.
      </p>
      <div className="rollt">
        <table className="liste mattabelle">
          <thead>
            <tr><th style={{ width: 56 }}></th><th style={{ width: 96 }}>Mat-Nr</th><th>Bezeichnung</th>
                <th className="zahl" style={{ width: 120 }}>Bestand</th><th className="zahl" style={{ width: 140 }}>Gezählt</th></tr>
          </thead>
          <tbody>
            {sicht.map((z) => (
              <tr key={z.artikel_id}>
                <td><ZoomBild id={z.bild_id} titel={z.kurztext} /></td>
                <td className="klein">{z.mat_nr}</td>
                <td>{z.kurztext}{z.kategorie && <div className="klein leise">{z.kategorie}</div>}</td>
                <td className="zahl">{menge(Number(z.bestand))} {z.einheit}</td>
                <td className="zahl">
                  <input style={{ width: 90, textAlign: 'right' }} inputMode="decimal" value={ist[z.artikel_id] ?? ''}
                         aria-label={`Gezählt ${z.mat_nr}`} onChange={(e) => setIst({ ...ist, [z.artikel_id]: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft || !eingetragen.length} onClick={speichern}>
          {eingetragen.length ? `${eingetragen.length} Zählungen buchen` : 'Zählungen buchen'}
        </button>
      </div>
    </Karte>
  );
}

// ============================================================================
// Übernahme
// ============================================================================

function Uebernahme() {
  const [d, setD] = useState<{ laeufe: any[]; stand: any } | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  async function laden() {
    try { setD(await api.materialUebernahmen()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  async function hoch(d: File) {
    setLaeuft(true); setFehler(null);
    try { await api.materialUebernehmen(d); await laden(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <>
      <Karte titel="Bisherige Materialliste übernehmen">
        <Fehlerkasten lage={fehler} />
        <p className="klein leise" style={{ marginTop: 0 }}>
          Die Materialliste als XLSX herunterladen und hier ablegen. Übernommen werden Katalog,
          Standardartikel, Bestand, Bestellungen, Wareneingang, Entnahmen, Inventur und die
          Telegram-Gruppen der Firmen. Das geht beliebig oft: nichts wird doppelt angelegt. Den
          Stand je Artikel setzt die Übernahme nur, solange in der Plattform noch nichts gebucht ist.
        </p>
        <Abwurf accept=".xlsx" laeuft={laeuft} laufText="Die Liste wird übernommen …"
          titel="Materialliste (XLSX) hier ablegen"
          hinweis="Bilder und PDF der Bestellungen kommen über n8n dazu."
          nimm={(st) => st[0] && hoch(st[0].datei)} />
        {d?.stand && (
          <dl className="felder" style={{ marginTop: 12 }}>
            <dt>Bilder da</dt><dd>{d.stand.bilder} Bilder an {d.stand.artikel_mit_bild} Artikeln</dd>
            <dt>PDF da</dt><dd>{d.stand.pdf_da} Bestellungen{d.stand.pdf_fehlt ? `, ${d.stand.pdf_fehlt} warten noch auf ihre PDF` : ''}</dd>
          </dl>
        )}
      </Karte>
      {(d?.laeufe ?? []).map((l, i) => <Bericht key={l.id} lauf={l} offen={i === 0} />)}
    </>
  );
}

function Bericht({ lauf, offen }: { lauf: any; offen: boolean }) {
  const b = lauf.bericht ?? {};
  const [auf, setAuf] = useState(offen);
  const z = (x: any) => menge(Number(x ?? 0));
  return (
    <Karte titel={`Übernahme vom ${zeitpunkt(lauf.zeit)}${lauf.weg === 'schnittstelle' ? ' (n8n)' : lauf.von ? ` (${lauf.von})` : ''}`}
           rechts={<button className="knopf klein" onClick={() => setAuf(!auf)}>{auf ? 'zuklappen' : 'aufklappen'}</button>}>
      {auf && (
        <>
          <dl className="felder">
            <dt>Katalog</dt><dd>{b.katalogstand ?? 'ohne Stand'}</dd>
            <dt>Artikel</dt><dd>{z(b.artikel?.neu)} neu, {z(b.artikel?.geaendert)} geändert, {z(b.artikel?.gleich)} gleich{b.artikel?.von_hand ? `, ${z(b.artikel.von_hand)} von Hand geändert und deshalb nicht überschrieben` : ''}</dd>
            <dt>Bestellungen</dt><dd>{z(b.bestellungen?.neu)} neu, {z(b.bestellungen?.geaendert)} geändert, {z(b.bestellungen?.gleich)} gleich, {z(b.bestellungen?.positionen)} Positionen</dd>
            <dt>Wareneingang</dt><dd>{z(b.wareneingang?.neu)} neu, {z(b.wareneingang?.gleich)} schon da</dd>
            <dt>Entnahmen</dt><dd>{z(b.entnahmen?.neu)} neu, {z(b.entnahmen?.gleich)} schon da</dd>
            <dt>Inventur</dt><dd>{z(b.inventur?.neu)} neu, {z(b.inventur?.gleich)} schon da</dd>
            <dt>Stand</dt><dd>{b.stand?.gesetzt ? `für ${z(b.stand.artikel)} Artikel gesetzt` : (b.stand?.grund ?? 'nicht gesetzt')}</dd>
            <dt>Bilder</dt><dd>{z(b.bilder_erwartet)} in der Liste zugeordnet</dd>
            <dt>PDF</dt><dd>{z(b.pdf_erwartet)} Bestellungen mit PDF</dd>
          </dl>
          {!!b.firmen?.ohne?.length && (
            <div className="hinweis">
              Keiner Firma zuzuordnen: {b.firmen.ohne.join(', ')}. Name der Firma unter Stammdaten prüfen und noch einmal übernehmen.
            </div>
          )}
          {!!b.entnahmen?.ohne_firma?.length && (
            <div className="hinweis">Entnahmen ohne zugeordnete Firma: {b.entnahmen.ohne_firma.join(', ')}.</div>
          )}
          {!!b.stand?.negativ?.length && (
            <div className="hinweis">Unter null übernommen: {b.stand.negativ.join(', ')}.</div>
          )}
          {!!b.artikel?.manuell?.length && (
            <p className="klein leise">Nicht im Telekom-Katalog, als eigene Artikel angelegt: {b.artikel.manuell.join('; ')}.</p>
          )}
          {!!b.hinweise?.length && <ul className="klein">{b.hinweise.map((h: string, i: number) => <li key={i}>{h}</li>)}</ul>}
        </>
      )}
    </Karte>
  );
}
